import { useCallback, useEffect, useState } from 'react'

import { useQuery, useReactiveVar } from '@apollo/client'
import { add, differenceInDays, format, parseISO } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'

import { locationData, store, timezoneAbbr } from '../cache'
import { APPOINTMENT_AVAILABILITY } from '../queries/appointmentAvailability'
import { toISOStringDate } from '../utils/toISOStringDate'

export const useAvailability = (dayRange = 60, currentDate) => {
  const today = new Date()
  const [startDate, setStartDate] = useState(today)
  const endDate = add(startDate, { days: dayRange })
  const { locationId } = useReactiveVar(store)
  const [appointments, setAppointments] = useState([])

  const { data, loading, error } = useQuery(APPOINTMENT_AVAILABILITY, {
    variables: {
      id: locationId,
      startDateUtc: toISOStringDate(startDate),
      endDateUtc: toISOStringDate(endDate),
    },
  })

  useEffect(() => {
    if (!loading && !error) {
      const fetchedAppointments = data?.location?.availableAppointments
      if (fetchedAppointments.length === 0) {
      } else {
        // Set target timezone based on practice location
        const targetTimezone = locationData()?.timezone

        // Map over fetched appointments array and reshape appointment object
        const formattedAppointments = fetchedAppointments.map(appointment => {
          // Convert UTC timestamp to a date object
          const utcDate = parseISO(appointment.start + 'Z') // Append Z for timezone designator

          // Format the appointment data for display
          const date = format(utcDate, 'yyyy-MM-dd')
          const startTime = formatInTimeZone(
            utcDate,
            targetTimezone,
            'HH:mm:ss'
          )
          const timezone = formatInTimeZone(utcDate, targetTimezone, 'zzz')

          // Store the timezone abbreviation globally for use in other components
          timezoneAbbr(timezone)

          // Store formatted appointment data
          return {
            date,
            startTime,
            timezone,
            ...appointment,
          }
        })
        // Combine fetched appointments with existing appointments
        const combinedAppointments = formattedAppointments.concat(appointments)
        setAppointments(combinedAppointments)
      }
    }
  }, [data, loading, error])

  const hasAvailability = !loading && !error && appointments.length > 0

  const loadNextBatch = useCallback(() => {
    setStartDate(endDate)
  }, [endDate])

  useEffect(() => {
    if (differenceInDays(currentDate, endDate) > 0) {
      loadNextBatch()
    }
  }, [currentDate, endDate])

  return {
    appointments,
    hasAvailability,
    loading,
    error,
  }
}
